/* #150 combined PDP: pure buy-box logic (no DOM). Loaded before assets/dpc-buybox.js; unit-tested in
   scratchpad/tests/unit/dpc-buybox-core.test.js. Choice names follow docs/analytics/ga4-conventions.md. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DPCBuybox = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var FIXED = {
    'size': 'size',
    'dip / curve shape': 'curve',
    'curve shape': 'curve',
    'base fabric': 'base_fabric',
    'wood finish': 'wood_finish',
    'felt colour': 'felt',
    'platform colour': 'platform'
  };

  function snake(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  // Shopify option name -> shared choice name. "Colour" on a CA shelf is the upper cushion fabric.
  function rowNameFor(optionName, ctx) {
    var k = String(optionName || '').trim().toLowerCase();
    if (FIXED[k]) return FIXED[k];
    if (k === 'colour' || k === 'color') return ctx && ctx.isShelf ? 'upper_fabric' : 'colour';
    return snake(k);
  }

  // Required rows without a value, in row order.
  function openRows(rows, state) {
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (r.required && !state[r.name]) out.push(r.name);
    }
    return out;
  }

  // The variant whose options equal every variant row's value; null until all are answered.
  function matchVariant(variants, rows, state) {
    var axes = rows.filter(function (r) { return r.kind === 'variant'; });
    for (var i = 0; i < axes.length; i++) if (!state[axes[i].name]) return null;
    for (var j = 0; j < variants.length; j++) {
      var v = variants[j], ok = true;
      for (var a = 0; a < axes.length; a++) {
        if (v.options[axes[a].option_index] !== state[axes[a].name]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  // Cheapest variant that fits the variant rows answered so far, and whether the price can still change: the price
  // follows the size as soon as it is chosen (review 4, F1). null when no variant fits.
  function fromPrice(variants, rows, state) {
    var axes = rows.filter(function (r) { return r.kind === 'variant' && state[r.name]; });
    var fit = variants.filter(function (v) {
      for (var a = 0; a < axes.length; a++) if (v.options[axes[a].option_index] !== state[axes[a].name]) return false;
      return true;
    });
    if (!fit.length) return null;
    var min = Infinity, max = -Infinity;
    fit.forEach(function (v) { min = Math.min(min, v.price); max = Math.max(max, v.price); });
    return { price: min, varies: max > min };
  }

  // What each option of a variant row leads to, given the other rows answered so far (review 4, F7): 'ok', 'soldout'
  // (it exists, none in stock) or 'none' (no such combination), with the answered row that rules it out.
  function optionStatus(variants, rows, state, rowName) {
    var row = null;
    for (var i = 0; i < rows.length; i++) if (rows[i].name === rowName) row = rows[i];
    var out = {};
    if (!row || row.kind !== 'variant') return out;
    var others = rows.filter(function (r) { return r.kind === 'variant' && r.name !== rowName && state[r.name]; });
    var fits = function (val, axes) {
      return variants.filter(function (v) {
        if (v.options[row.option_index] !== val) return false;
        for (var a = 0; a < axes.length; a++) if (v.options[axes[a].option_index] !== state[axes[a].name]) return false;
        return true;
      });
    };
    var vals = row.values ? row.values.map(function (x) { return x.value; }) : [];
    if (!vals.length) variants.forEach(function (v) { if (vals.indexOf(v.options[row.option_index]) < 0) vals.push(v.options[row.option_index]); });
    vals.forEach(function (val) {
      var fit = fits(val, others);
      if (!fit.length) {
        var clash = others.length ? others[0].name : null;
        for (var k = 0; k < others.length; k++) {
          if (fits(val, others.filter(function (o, j) { return j !== k; })).length) { clash = others[k].name; break; }
        }
        out[val] = { status: 'none', clash: clash };
      } else {
        out[val] = { status: fit.some(function (v) { return v.available; }) ? 'ok' : 'soldout' };
      }
    });
    return out;
  }

  // Image whose alt ends "_<value>" (the theme's #Option_Value convention), else the fallback.
  function imageFor(images, rowName, value, fallback) {
    var want = String(value || '').trim().toLowerCase();
    for (var i = 0; i < (images || []).length; i++) {
      var alt = String(images[i].alt || '');
      var cut = alt.lastIndexOf('_');
      if (alt.charAt(0) === '#' && cut > 0 && alt.slice(cut + 1).trim().toLowerCase() === want) return images[i].src;
    }
    return fallback;
  }

  // The photo for a choice: the variant's own featured image (how the store attaches colour photos), else the
  // #Option_Value alt, else the fallback.
  // #94 wall sets have one variant, so a shelf colour's photo is the COMPONENT shelf's own (data.set_photos:
  // [{src, ratio, match: {rowName: value, ...}}]). Prefer the photo whose other keys agree with the current choices
  // (the Full Wall's cushion photo follows its wood finish), else the first for that value.
  // prefer: the cushion row to show when a choice fits several (the Full Wall's wood finish fits both shelves'
  // cushions): the one the shopper changed last, else shelf 1 (Test regression 2026-10-24 showed shelf 2's Beige).
  function setPhotoFor(setPhotos, row, value, state, prefer) {
    var first = null, agreed = null;
    for (var i = 0; i < (setPhotos || []).length; i++) {
      var m = setPhotos[i].match || {};
      if (!row || m[row.name] !== value) continue;
      if (!first) first = setPhotos[i].src;
      var agrees = Object.keys(m).every(function (k) { return k === row.name || !state || !state[k] || state[k] === m[k]; });
      if (!agrees) continue;
      if (!prefer || m[prefer] !== undefined) return setPhotos[i].src;
      if (!agreed) agreed = setPhotos[i].src;
    }
    return agreed || first;
  }

  function photoFor(variants, images, row, value, fallback, setPhotos, state, prefer) {
    if (setPhotos && setPhotos.length) {
      var sp = setPhotoFor(setPhotos, row, value, state, prefer || 'shelf_1_upper_fabric');
      if (sp) return sp;
    }
    if (row && row.kind === 'variant' && row.option_index != null) {
      for (var i = 0; i < (variants || []).length; i++) {
        var v = variants[i];
        if (v.image && v.options[row.option_index] === value) return v.image;
      }
    }
    return imageFor(images, row && row.name, value, fallback);
  }

  // True when every value of the row has a photo of its own (distinct): only then is there a photo to show while the
  // shopper picks (Martin 2026-10-21).
  function rowHasPhotos(variants, images, row, setPhotos) {
    var vals = (row && row.values) || [];
    if (vals.length < 2) return false;
    var seen = {};
    for (var i = 0; i < vals.length; i++) {
      var src = photoFor(variants, images, row, vals[i].value, null, setPhotos, null);
      if (!src || seen[src]) return false;
      seen[src] = true;
    }
    return true;
  }

  // How the phone strip crops a photo: custom.pin_crop when set ("x% y%" or "whole"), else by shape. Portrait photos
  // hold the subject high; square and landscape packshots are centred (230 colour photos checked 2026-10-21).
  function pinCrop(ratio, override) {
    if (override === 'whole') return { fit: 'contain', pos: '50% 50%' };
    if (typeof override === 'string' && /^\d{1,3}% \d{1,3}%$/.test(override)) return { fit: 'cover', pos: override };
    return { fit: 'cover', pos: ratio && ratio < 0.95 ? '50% 30%' : '50% 50%' };
  }

  function pinHeight(innerHeight) {
    return Math.round(Math.min(260, Math.max(180, 0.3 * innerHeight)));
  }

  function addResult(status, requested, added) {
    if (status >= 200 && status < 300 && added >= requested) return 'ok';
    if (added > 0) return 'partial';
    return 'error';
  }

  function deviceOf(innerWidth) {
    return innerWidth <= 749 ? 'mobile' : 'desktop';
  }

  function totalPence(variant, addon) {
    var t = variant ? variant.price : 0;
    if (addon && addon.variant) t += addon.variant.price * (addon.qty || 0);
    return t;
  }

  return {
    rowNameFor: rowNameFor, openRows: openRows, matchVariant: matchVariant, imageFor: imageFor,
    fromPrice: fromPrice, optionStatus: optionStatus, photoFor: photoFor, rowHasPhotos: rowHasPhotos, pinCrop: pinCrop, pinHeight: pinHeight, addResult: addResult, deviceOf: deviceOf, totalPence: totalPence
  };
});
